import { type MutableRefObject, useEffect, useRef, useState } from 'react';

/**
 * React hook for checking whether a referenced element is in the viewport or not.
 * The viewport is based on the window.
 * @internal
 */
export function useIsInViewport<T>(): [MutableRefObject<T | null>, boolean] {
  const containerRef = useRef(null);
  const [isVisible, setIsVisible] = useState(true);

  const callback = (entries: IntersectionObserverEntry[]) => {
    for (const entry of entries) {
      if (entry.target === containerRef.current) {
        setIsVisible(entry.isIntersecting);
        break;
      }
    }
  };

  useEffect(() => {
    const refElement = containerRef.current;
    const observer = new IntersectionObserver(callback);
    if (refElement) {
      observer.observe(refElement);
    }

    return () => {
      // clean up
      if (refElement) {
        observer.unobserve(refElement);
      }
    };
  }, [containerRef]);

  return [containerRef, isVisible];
}
